import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, ViewChildren, QueryList, TemplateRef, ContentChildren, AfterContentInit, OnChanges, SimpleChanges, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { PaginatorModule } from "primeng/paginator";
import { SkeletonModule } from "primeng/skeleton";
import { InfiniteScrollDirective } from "ngx-infinite-scroll";
import { TableColumn, TableConfig, SORT_ORDER } from "../../models/table.models";
import { SkeletonService } from "../../../contract-management/services/skeleton.service";
import { TextCasePipe } from "../../pipes/textCase.pipe";
import { InputComponent } from "../../../@core/components/ui/input/input.component";
import { IconComponent } from "../../../@core/components/ui/icon/icon.component";

@Component({
    selector: "app-data-table",
    imports: [
        CommonModule,
        FormsModule,
        PaginatorModule,
        SkeletonModule,
        InfiniteScrollDirective,
        TextCasePipe,
        InputComponent,
        IconComponent,
    ],
    standalone: true,
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent implements OnInit, OnDestroy, AfterContentInit, OnChanges {
    @Input() data: any[] = [];
    @Input() columns: TableColumn[] = [];
    @Input() config: TableConfig = {};
    @Input() dataKey: string = "id";
    @Input() tableClass: string = "";
    @Input() rowClass: string | ((data: any, index: number) => string) = "";
    @Input() customEmptyTemplate?: TemplateRef<any>;
    @Input() customTemplates: { [key: string]: TemplateRef<any> } = {};
    @Input() showScrollBar: boolean = false;

    // External pagination support
    @Input() externalPagination: boolean = false;
    @Input() externalFirst: number = 0;
    @Input() externalTotalRecords: number = 0;
    @Output() pageChange = new EventEmitter<any>();

    @Output() selectionChange = new EventEmitter<any[]>();
    @Output() rowSelect = new EventEmitter<any>();
    @Output() rowUnselect = new EventEmitter<any>();
    @Output() rowClick = new EventEmitter<any>();
    @Output() scrolled = new EventEmitter<void>();
    @Output() headerChange = new EventEmitter<{ field: string; header: string }>();

    @ContentChildren(TemplateRef, { descendants: true }) templates!: QueryList<TemplateRef<any>>;
    @ViewChildren(InputComponent) headerInputs!: QueryList<InputComponent>;

    // Internal state
    selectedRows: any[] = [];
    globalFilterValue: string = '';
    filteredData: any[] = [];
    paginatedData: any[] = [];
    first: number = 0; // PrimeNG paginator uses 'first' instead of 'currentPage'
    globalFilterFields: string[] = [];
    private templateMap = new Map<string, TemplateRef<any>>();
    currentSortField: string | null = null;
    currentSortOrder: SORT_ORDER = SORT_ORDER.NONE;

    // Accordion state — tracks the dataKey values of currently expanded rows.
    private expandedRowKeys = new Set<any>();

    // Editable header state
    editingHeaderField: string | null = null;
    editingHeaderValue: string = '';

    // Skeleton properties
    skeletonCount: number = 5;

    constructor(
        private cdr: ChangeDetectorRef,
        private skeletonService: SkeletonService
    ) { }

    ngOnInit() {
        this.setupDefaultConfig();
        this.setupGlobalFilterFields();
        this.updateData();
        this.setupResizeObserver();
        this.calculateSkeletonCount(); // Initialize skeleton count
    }

    ngOnDestroy() {
        this.cleanupResizeObserver();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['data'] || changes['columns']) {
            this.updateData();
        }
        if (changes['externalFirst'] && this.externalPagination) {
            this.first = this.externalFirst;
        }
    }

    ngAfterContentInit() {
        // Map templates by their template reference variables
        this.templates.forEach((template: any) => {
            const templateName = template.elementRef?.nativeElement?.getAttribute?.('template') ||
                template.elementRef?.nativeElement?.id ||
                template._declarationTContainer?.localNames?.[1];

            if (templateName) {
                this.templateMap.set(templateName, template);
            }
        });
    }

    private setupDefaultConfig() {
        this.config = {
            selectable: false,
            paginator: true,
            rows: 10,
            rowsPerPageOptions: [5, 10, 20, 50],
            loading: false,
            emptyMessage: "No data found",
            globalFilter: false,
            ...this.config
        };
    }

    private setupGlobalFilterFields() {
        this.globalFilterFields = this.columns
            .filter(col => col.filterable !== false)
            .map(col => col.field);
    }

    private updateData() {
        this.filteredData = this.filterData();
        this.updatePagination();
    }

    private filterData(): any[] {
        if (!this.globalFilterValue || !this.config.globalFilter) {
            return [...this.data];
        }

        const filterValue = this.globalFilterValue.toLowerCase();
        return this.data.filter(item => {
            return this.globalFilterFields.some(field => {
                const value = this.getFieldValue(item, field);
                return value && value.toString().toLowerCase().includes(filterValue);
            });
        });
    }

    private updatePagination() {
        if (!this.config.paginator || this.externalPagination) {
            // For external pagination, show all provided data (already paginated from server)
            this.paginatedData = [...this.filteredData];
            return;
        }

        const startIndex = this.first;
        const endIndex = startIndex + this.config.rows!;
        this.paginatedData = this.filteredData.slice(startIndex, endIndex);
    }

    // Getters
    get visibleColumns(): TableColumn[] {
        return this.columns.filter(col => col.visible !== false);
    }

    get totalColumns(): number {
        let count = this.visibleColumns.length;
        if (this.config.selectable) count++;
        return count;
    }

    get currentFirst(): number {
        return this.externalPagination ? this.externalFirst : this.first;
    }

    get totalRecords(): number {
        return this.externalPagination ? this.externalTotalRecords : this.filteredData.length;
    }

    // Utility methods
    getFieldValue(obj: any, path: string): any {
        return path.split(".").reduce((current, prop) => current?.[prop], obj);
    }

    getTemplate(templateName: string, column?: TableColumn): TemplateRef<any> | null {
        if (this.customTemplates[templateName]) {
            return this.customTemplates[templateName];
        }
        return this.templateMap.get(templateName) || null;
    }

    getRowClass(rowData: any, index: number): string {
        let classes = '';
        if (typeof this.rowClass === "function") {
            classes = this.rowClass(rowData, index);
        } else {
            classes = this.rowClass;
        }

        // Add selected class if row is selected
        if (this.isRowSelected(rowData)) {
            classes += ' selected-row';
        }

        // Add clickable styling if rowClick has observers
        if (this.rowClick.observed) {
            classes += ' cursor-pointer hover:bg-gray-50 transition-colors';
        }

        return classes;
    }

    getBadgeClass(value: any): string {
        const badgeClasses: { [key: string]: string } = {
            active: "bg-green-100 text-green-800",
            inactive: "bg-red-100 text-red-800",
            pending: "bg-yellow-100 text-yellow-800",
            completed: "bg-blue-100 text-blue-800",
            processing: "bg-purple-100 text-purple-800"
        };

        return badgeClasses[value?.toLowerCase()] || "bg-gray-100 text-gray-800";
    }

    // Event handlers
    onGlobalFilter(event: any) {
        this.globalFilterValue = event.target.value;
        this.first = 0; // Reset to first page
        this.updateData();
    }

    handleColumnSort(column: TableColumn) {
        if (!column.sortable) return;

        if (this.currentSortField === column.field) {
            // Cycle through sort orders: desc -> asc -> null
            if (this.currentSortOrder === SORT_ORDER.DESC) {
                this.currentSortOrder = SORT_ORDER.ASC;
            } else if (this.currentSortOrder === SORT_ORDER.ASC) {
                this.currentSortOrder = SORT_ORDER.NONE;
                this.currentSortField = null;
            }
        } else {
            // New column, start with descending
            this.currentSortField = column.field;
            this.currentSortOrder = SORT_ORDER.DESC;
        }

        this.config.columnSortingFunction?.(this.currentSortField || '', this.currentSortOrder);

        // Update the data after sorting to refresh the table display
        this.updateData();
    }

    getSortIcon(column: TableColumn): string {
        if (!column.sortable) return '';

        if (this.currentSortField !== column.field) {
            return 'pi pi-sort';
        }

        switch (this.currentSortOrder) {
            case SORT_ORDER.ASC:
                return 'pi pi-sort-up';
            case SORT_ORDER.DESC:
                return 'pi pi-sort-down';
            default:
                return 'pi pi-sort';
        }
    }

    isColumnSortable(column: TableColumn): boolean {
        return column.sortable === true && this.config.columnSortingFunction !== undefined;
    }

    onPageChange(event: any) {
        if (this.externalPagination) {
            this.pageChange.emit(event);
        } else {
            // Check if rows per page has changed
            const rowsChanged = event.rows && event.rows !== this.config.rows;

            if (rowsChanged) {
                // Reset to first page when rows per page changes
                this.first = 0;
                this.config.rows = event.rows;
            } else {
                // Normal page navigation
                this.first = event.first;
            }

            this.updatePagination();
        }
    }

    onRowClick(rowData: any, event: Event) {
        // Don't handle row click if the click target is a checkbox (to avoid double selection)
        const targetElement = event.target as HTMLElement;
        if (targetElement.tagName === 'INPUT') {
            return;
        }

        if (targetElement.closest('app-select, app-toggle, app-button')) {
            return;
        }

        // Toggle accordion expansion when clicking the row (unless only the chevron button should do it).
        // Rows without children are not expandable, so clicking them is a no-op for the accordion.
        if (this.config.accordion && this.config.accordionRowClick !== false && this.hasChildren(rowData)) {
            this.toggleAccordion(rowData);
        }

        // Emit row click event for external handling
        this.rowClick.emit(rowData);

        // Handle selection on row click only when config allows (when false, only checkbox click selects)
        if (this.config.selectable && this.config.selectionOnRowClick !== false) {
            this.toggleRowSelection(rowData, event);
        }
    }

    // ── Accordion helpers ───────────────────────────────────────────────────

    /**
     * Toggles the expanded/collapsed state of a row in accordion mode.
     * The `event` parameter is optional — pass it from the chevron button click
     * to stop propagation and prevent double-toggling with the row click handler.
     * No-ops for rows that have no children (nothing to expand).
     */
    toggleAccordion(rowData: any, event?: Event) {
        event?.stopPropagation();
        if (!this.hasChildren(rowData)) {
            return;
        }
        const key = this.getFieldValue(rowData, this.dataKey) ?? JSON.stringify(rowData);
        if (this.expandedRowKeys.has(key)) {
            this.expandedRowKeys.delete(key);
        } else {
            this.expandedRowKeys.add(key);
        }
    }

    /** Returns true when the given row is currently expanded. */
    isExpanded(rowData: any): boolean {
        const key = this.getFieldValue(rowData, this.dataKey) ?? JSON.stringify(rowData);
        return this.expandedRowKeys.has(key);
    }

    /**
     * Returns the child rows array for the given parent row.
     * Uses `config.childrenField` (default `'children'`).
     */
    getChildren(rowData: any): any[] {
        const field = this.config.childrenField ?? 'children';
        const children = this.getFieldValue(rowData, field);
        return Array.isArray(children) ? children : [];
    }

    /**
     * True when the row has more than one child row.
     * A single child is redundant with the parent row (nothing extra to reveal),
     * so the expand chevron only appears once there are genuinely multiple
     * sub-rows to break down.
     */
    hasChildren(rowData: any): boolean {
        return this.getChildren(rowData).length > 1;
    }

    /**
     * Returns the column definitions used for child rows.
     * Falls back to the visible parent columns when `config.childColumns` is absent.
     */
    getChildColumns(): TableColumn[] {
        return this.config.childColumns ?? this.visibleColumns;
    }

    onScrolled() {
        this.scrolled.emit();
    }

    // Editable header methods
    startHeaderEdit(col: TableColumn, event: Event) {
        event.stopPropagation();
        this.editingHeaderField = col.field;
        this.editingHeaderValue = col.header;
        setTimeout(() => {
            this.headerInputs.first?.focusInput();
        }, 0);
    }

    saveHeaderEdit(col: TableColumn) {
        const newValue = this.editingHeaderValue.trim();
        if (newValue && newValue !== col.header) {
            col.header = newValue;
            this.headerChange.emit({ field: col.field, header: newValue });
        }
        this.editingHeaderField = null;
        this.editingHeaderValue = '';
    }

    cancelHeaderEdit() {
        this.editingHeaderField = null;
        this.editingHeaderValue = '';
    }

    // Selection methods
    isRowSelected(rowData: any): boolean {
        return this.selectedRows.some(selected =>
            this.getFieldValue(selected, this.dataKey) === this.getFieldValue(rowData, this.dataKey)
        );
    }

    toggleRowSelection(rowData: any, event: Event) {
        event.stopPropagation();

        // Check if row is selectable
        if (this.config.isRowSelectable && !this.config.isRowSelectable(rowData)) {
            return;
        }

        if (this.isRowSelected(rowData)) {
            this.selectedRows = this.selectedRows.filter(selected =>
                this.getFieldValue(selected, this.dataKey) !== this.getFieldValue(rowData, this.dataKey)
            );
            this.rowUnselect.emit(rowData);
        } else {
            this.selectedRows = [...this.selectedRows, rowData];
            this.rowSelect.emit(rowData);
        }

        this.selectionChange.emit([...this.selectedRows]);
    }

    isRowSelectable(rowData: any): boolean {
        if (this.config.isRowSelectable) {
            return this.config.isRowSelectable(rowData);
        }
        return true; // Default to selectable if no function provided
    }

    isAllSelected(): boolean {
        const selectableRows = this.paginatedData.filter(row => this.isRowSelectable(row));
        return selectableRows.length > 0 &&
            selectableRows.every(row => this.isRowSelected(row));
    }

    isSomeSelected(): boolean {
        const selectableRows = this.paginatedData.filter(row => this.isRowSelectable(row));
        const selectedSelectableRows = selectableRows.filter(row => this.isRowSelected(row));
        return selectedSelectableRows.length > 0 && selectedSelectableRows.length < selectableRows.length;
    }

    /** Called when the selection column header cell is clicked (whole cell is clickable). */
    onSelectionHeaderCellClick(event: Event) {
        event.stopPropagation();
        const selectAll = !this.isAllSelected();
        this.toggleAllSelection({ target: { checked: selectAll } } as unknown as Event);
    }

    /** Called when the selection column body cell is clicked (whole cell is clickable); ignores clicks on the checkbox to avoid double-toggle. */
    onSelectionCellClick(rowData: any, event: Event) {
        event.stopPropagation();
        if ((event.target as HTMLElement).tagName === 'INPUT') return;
        this.toggleRowSelection(rowData, event);
    }

    toggleAllSelection(event: Event) {
        const target = event.target as HTMLInputElement;

        if (target.checked) {
            // Select all selectable visible rows
            this.paginatedData.forEach(row => {
                if (this.isRowSelectable(row) && !this.isRowSelected(row)) {
                    this.selectedRows.push(row);
                }
            });
        } else {
            // Deselect all visible rows
            this.paginatedData.forEach(row => {
                this.selectedRows = this.selectedRows.filter(selected =>
                    this.getFieldValue(selected, this.dataKey) !== this.getFieldValue(row, this.dataKey)
                );
            });
        }

        this.selectionChange.emit([...this.selectedRows]);
    }

    // Public methods to match original interface
    clearSelection() {
        this.selectedRows = [];
        this.selectionChange.emit([]);
    }

    clearSorting() {
        this.currentSortField = null;
        this.currentSortOrder = SORT_ORDER.NONE;
        this.config.columnSortingFunction?.(this.currentSortField || '', this.currentSortOrder);
    }

    refresh() {
        this.globalFilterValue = '';
        this.first = 0;
        this.selectedRows = [];
        this.clearSorting();
        this.updateData();
        this.selectionChange.emit([]);
    }

    exportData(format: "csv" | "excel" = "csv") {
        if (format === "csv") {
            this.exportToCSV();
        }
    }

    private exportToCSV() {
        const headers = this.visibleColumns.map(col => col.header).join(',');
        const rows = this.filteredData.map(row =>
            this.visibleColumns.map(col => {
                const value = this.getFieldValue(row, col.field);
                return `"${value || ''}"`;
            }).join(',')
        );

        const csv = [headers, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'table-data.csv';
        link.click();
        window.URL.revokeObjectURL(url);
    }

    //#region skeleton operations
    private setupResizeObserver() {
        this.skeletonService.setupResizeObserver(() => {
            this.calculateSkeletonCount();
            this.cdr.detectChanges();
        });
    }

    private cleanupResizeObserver() {
        this.skeletonService.cleanupResizeObserver();
    }

    calculateSkeletonCount() {
        // Estimate table row height (assuming standard table row height)
        const rowHeight = 3.5 * 16; // ~3.5rem converted to pixels (assuming 1rem = 16px)
        const gap = 0; // No gap between table rows
        const padding = 8 * 16; // Account for table header, pagination, and other padding

        this.skeletonCount = this.skeletonService.calculateSkeletonCount(rowHeight, gap, padding);
    }

    getSkeletonCount(): number[] {
        return this.skeletonService.getSkeletonArray(this.skeletonCount);
    }
    //#endregion
}
